import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { usePayPointCredentialQuery } from "Api/Queries/PayPoints/usePayPointCredentialQuery";
import { useGenerateGatewayCredentialMutation } from "Api/Mutations/PayPoints/useGenerateGatewayCredentialMutation";
import { useRegenerateGatewayCredentialMutation } from "Api/Mutations/PayPoints/useRegenerateGatewayCredentialMutation";
import { useRevokeGatewayCredentialMutation } from "Api/Mutations/PayPoints/useRevokeGatewayCredentialMutation";
import { GatewaySecretDialog } from "Components/Paypoints/GatewaySecretDialog";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { FormSection } from "Components/Shared/FormSection";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";
import { toast } from "sonner";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.PaymentGateways;

type Props = {
  payPointPublicID: string;
};

type SecretState = { title: string; clientID: string; clientSecret: string };

export const PaypointGatewaySection = ({ payPointPublicID }: Props) => {
  const { t } = useResource();
  const { data: credential, isLoading } =
    usePayPointCredentialQuery(payPointPublicID);
  const [secret, setSecret] = useState<SecretState | null>(null);

  const {
    isOpen: isRegenerateOpen,
    open: openRegenerate,
    handleResult: handleRegenerateResult,
  } = useConfirmDialog();
  const {
    isOpen: isRevokeOpen,
    open: openRevoke,
    handleResult: handleRevokeResult,
  } = useConfirmDialog();

  const { mutate: generate, isPending: isGenerating } =
    useGenerateGatewayCredentialMutation({
      payPointPublicID,
      onSuccess: (result) =>
        setSecret({
          title: t(RESOURCES.Secret.CreatedTitle),
          clientID: result.clientID,
          clientSecret: result.clientSecret,
        }),
      onError: () => toast.error(t(RESOURCES.Messages.GenerateError)),
    });

  const { mutate: regenerate } = useRegenerateGatewayCredentialMutation({
    payPointPublicID,
    onSuccess: (result) =>
      setSecret({
        title: t(RESOURCES.Secret.RegeneratedTitle),
        clientID: result.clientID,
        clientSecret: result.clientSecret,
      }),
    onError: () => toast.error(t(RESOURCES.Messages.RegenerateError)),
  });

  const { mutate: revoke } = useRevokeGatewayCredentialMutation({
    payPointPublicID,
    onSuccess: () => toast.success(t(RESOURCES.Messages.RevokeSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.RevokeError)),
  });

  const hasCredential = !!credential?.publicID;

  return (
    <FormSection legend={t(RESOURCES.Legend)}>
      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.Description)}
        </Typography>

        {isLoading ? (
          <Skeleton height={48} />
        ) : hasCredential ? (
          <Stack gap={1}>
            <Stack>
              <Typography variant="body2" color="text.secondary">
                {t(RESOURCES.ClientID)}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                >
                  {credential?.publicID}
                </Typography>
                <CopyToClipboard
                  text={credential?.publicID ?? ""}
                  accessibilityLabel={t(RESOURCES.CopyClientID)}
                  successMessage={t(RESOURCES.ClientIDCopied)}
                  size={18}
                />
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t(RESOURCES.LastUsedAt)}:{" "}
              {credential?.lastUsedAt
                ? formatDate(credential.lastUsedAt, true)
                : "—"}
            </Typography>
            <Stack direction="row" gap={1} mt={1}>
              <Button variant="outlined" onClick={openRegenerate}>
                {t(RESOURCES.Regenerate)}
              </Button>
              <Button variant="outlined" color="error" onClick={openRevoke}>
                {t(RESOURCES.Revoke)}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t(RESOURCES.NoCredential)}
            </Typography>
            <Button
              variant="contained"
              disabled={isGenerating}
              onClick={() => generate()}
            >
              {t(RESOURCES.Generate)}
            </Button>
          </Box>
        )}
      </Stack>

      {secret && (
        <GatewaySecretDialog
          isOpen
          title={secret.title}
          clientID={secret.clientID}
          clientSecret={secret.clientSecret}
          onClose={() => setSecret(null)}
        />
      )}

      <ConfirmDialog
        isOpen={isRegenerateOpen}
        handleResult={handleRegenerateResult(() => regenerate())}
        modalTitle={t(RESOURCES.Messages.RegenerateConfirmTitle)}
        modalContent={
          <Typography>{t(RESOURCES.Messages.RegenerateConfirm)}</Typography>
        }
      />
      <ConfirmDialog
        isOpen={isRevokeOpen}
        handleResult={handleRevokeResult(() => revoke())}
        modalTitle={t(RESOURCES.Messages.RevokeConfirmTitle)}
        modalContent={
          <Typography>{t(RESOURCES.Messages.RevokeConfirm)}</Typography>
        }
        isDestructive
      />
    </FormSection>
  );
};
